// src/pages/Admin/MenuConfigPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/auth-context";

type SubItemConfig = {
  label: string;
  codigo?: string;
  hidden?: boolean; // false = ativo, true = oculto
};

type MainItemConfig = {
  label: string;
  codigo?: string;
  hidden?: boolean;
  subitems: SubItemConfig[];
};

type DepartmentConfig = {
  name: string;
  slug?: string;
  ordem?: number;
  hidden?: boolean;
  items: MainItemConfig[];
};

type Selection = number | null; // índice do departamento selecionado

type CollaboratorConfig = {
  id: number;
  nome: string;
  email: string;
  departamentoSlug: string | null;
};

const removeAccents = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const norm = (s: string) => removeAccents(String(s)).toLowerCase();

const deptSlug = (name: string) =>
  removeAccents(name).toLowerCase().replace(/\s+/g, "-");

const MenuConfigPage: React.FC = () => {
  const { theme } = useTheme();
  const { sessao } = useAuth();

  const [menu, setMenu] = useState<DepartmentConfig[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDeptIndex, setSelectedDeptIndex] = useState<Selection>(null);
  const [search, setSearch] = useState("");

  const primaryColor = theme?.primary || "#114280";

  // Usuários atuais (exemplo inicial – 5 usuários)
  const [collaborators, setCollaborators] = useState<CollaboratorConfig[]>([
    {
      id: 1,
      nome: "Pietra Silva",
      email: "pietra.silva@googlemarine.com.br",
      departamentoSlug: "coordenacao",
    },
    {
      id: 2,
      nome: "Vitor Silva",
      email: "vitor.silva@googlemarine.com.br",
      departamentoSlug: "suprimentos",
    },
    {
      id: 3,
      nome: "Marina Costa",
      email: "marina.costa@googlemarine.com.br",
      departamentoSlug: "comercial",
    },
    {
      id: 4,
      nome: "João Almeida",
      email: "joao.almeida@googlemarine.com.br",
      departamentoSlug: "diretoria",
    },
    {
      id: 5,
      nome: "Bruna Ferraz",
      email: "bruna.ferraz@googlemarine.com.br",
      departamentoSlug: "recursos-humanos",
    },
  ]);

  // Toggle pequenininho, redondo e azul
  const ToggleDot: React.FC<{ checked: boolean; onToggle: () => void }> = ({
    checked,
    onToggle,
  }) => (
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: 16,
        height: 16,
        borderRadius: 999,
        border: `1px solid ${primaryColor}`,
        background: checked ? primaryColor : "#ffffff",
        cursor: "pointer",
        display: "inline-block",
        padding: 0,
      }}
    />
  );

  // ========== Carregar menu da API ==========
  useEffect(() => {
    let aborted = false;

    const loadMenu = async () => {
      if (menu) return; // já carregado

      let token = "";
      try {
        if (typeof window !== "undefined") {
          token = localStorage.getItem("access_token") || "";
        }
      } catch {
        token = "";
      }

      if (!token) {
        setError("Token (access_token) ausente no localStorage");
        setMenu(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const res = await fetch(
          "https://api.eva-googlemarine.com/geral/get-menu",
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();

        if (!data?.ok || !Array.isArray(data.menu)) {
          throw new Error("Resposta de menu inválida");
        }

        if (aborted) return;

        const mapped: DepartmentConfig[] = data.menu.map((dept: any) => ({
          name: dept.name,
          slug: dept.slug,
          ordem: dept.ordem,
          hidden: false,
          items: (dept.items || []).map((it: any) => ({
            label: it.label,
            codigo: it.codigo,
            hidden: false,
            subitems: (it.subitems || []).map((sub: any) => ({
              label: sub.label,
              codigo: sub.codigo,
              hidden: false,
            })),
          })),
        }));

        setMenu(mapped);
        if (mapped.length > 0) {
          setSelectedDeptIndex(0);
        }
      } catch (e: any) {
        console.error("Erro ao carregar menu:", e);
        if (!aborted) {
          setError(String(e?.message || e));
          setMenu(null);
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    loadMenu();
    return () => {
      aborted = true;
    };
  }, [menu]);

  // ========== Filtro de departamentos ==========
  const filteredDeptIndices = useMemo(() => {
    if (!menu) return [];
    const q = norm(search.trim());
    if (!q) return menu.map((_, idx) => idx);

    return menu
      .map((dept, idx) => {
        const matchesDept =
          norm(dept.name).includes(q) || norm(dept.slug || "").includes(q);

        const matchesItems = dept.items.some((it) => {
          if (norm(it.label).includes(q) || norm(it.codigo || "").includes(q)) {
            return true;
          }
          return it.subitems.some(
            (sub) =>
              norm(sub.label).includes(q) || norm(sub.codigo || "").includes(q)
          );
        });

        if (matchesDept || matchesItems) return idx;
        return null;
      })
      .filter((i): i is number => i !== null);
  }, [menu, search]);

  // ========== Helpers de update ==========
  const updateDepartment = (
    deptIndex: number,
    updater: (d: DepartmentConfig) => DepartmentConfig
  ) => {
    setMenu((prev) => {
      if (!prev) return prev;
      if (!prev[deptIndex]) return prev;
      const clone = [...prev];
      clone[deptIndex] = updater(clone[deptIndex]);
      return clone;
    });
  };

  const updateMainItem = (
    deptIndex: number,
    itemIndex: number,
    updater: (m: MainItemConfig) => MainItemConfig
  ) => {
    updateDepartment(deptIndex, (d) => {
      if (!d.items[itemIndex]) return d;
      const items = [...d.items];
      items[itemIndex] = updater(items[itemIndex]);
      return { ...d, items };
    });
  };

  const updateSubItem = (
    deptIndex: number,
    itemIndex: number,
    subIndex: number,
    updater: (s: SubItemConfig) => SubItemConfig
  ) => {
    updateMainItem(deptIndex, itemIndex, (m) => {
      if (!m.subitems[subIndex]) return m;
      const subitems = [...m.subitems];
      subitems[subIndex] = updater(subitems[subIndex]);
      return { ...m, subitems };
    });
  };

  // ========== Toggles simples ==========
  const toggleDeptActive = (deptIndex: number) => {
    updateDepartment(deptIndex, (d) => ({
      ...d,
      hidden: !d.hidden,
    }));
  };

  const toggleMainActive = (deptIndex: number, itemIndex: number) => {
    updateMainItem(deptIndex, itemIndex, (m) => ({
      ...m,
      hidden: !m.hidden,
    }));
  };

  const toggleSubActive = (
    deptIndex: number,
    itemIndex: number,
    subIndex: number
  ) => {
    updateSubItem(deptIndex, itemIndex, subIndex, (s) => ({
      ...s,
      hidden: !s.hidden,
    }));
  };

  // ========== Exportar JSON do menu ==========
  const handleExportJson = () => {
    if (!menu) return;
    const json = JSON.stringify(menu, null, 2);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(json);
        alert("Configuração do menu copiada para a área de transferência ✅");
      } else {
        console.log("Menu JSON:", json);
        alert("JSON do menu impresso no console do navegador.");
      }
    } catch (e) {
      console.error("Erro ao copiar JSON:", e);
      alert("Erro ao copiar JSON. Veja o console para o conteúdo.");
      console.log("Menu JSON:", json);
    }
  };

  // ========== Gestão de acesso: helpers ==========
  const handleUpdateCollaboratorDept = (id: number, slug: string) => {
    setCollaborators((prev) =>
      prev.map((c) => (c.id === id ? { ...c, departamentoSlug: slug } : c))
    );
  };

  const handleRemoveCollaborator = (id: number) => {
    setCollaborators((prev) => prev.filter((c) => c.id !== id));
  };

  const handleExportAccessJson = () => {
    const payload = collaborators.map((c) => ({
      id: c.id,
      nome: c.nome,
      email: c.email,
      departamentoSlug: c.departamentoSlug,
    }));

    const json = JSON.stringify(payload, null, 2);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(json);
        alert("Mapa de acesso copiado para a área de transferência ✅");
      } else {
        console.log("Acessos JSON:", json);
        alert("JSON de acessos impresso no console do navegador.");
      }
    } catch (e) {
      console.error("Erro ao copiar JSON de acessos:", e);
      alert("Erro ao copiar JSON. Veja o console para o conteúdo.");
      console.log("Acessos JSON:", json);
    }
  };

  const userDept = (sessao?.departamento || "").toLowerCase();
  const selectedDept =
    menu && selectedDeptIndex !== null ? menu[selectedDeptIndex] : null;

  // ========== Cálculo de licenças ==========
  const USERS_LIMIT = 10;
  const totalUsers = collaborators.length;
  const extraUsers = Math.max(0, totalUsers - USERS_LIMIT);
  const extraCost = extraUsers * 35;
  const usagePct = Math.min(100, (totalUsers / USERS_LIMIT) * 100);

  // ========== UI ==========
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        marginTop: 0,
        background: "#ffffff",
        color: "#000000",
        fontFamily:
          "'Poppins', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* CONTEÚDO GERAL */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          padding: 16,
          gap: 12,
          boxSizing: "border-box",
        }}
      >
        {/* Linha de topo com título */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 4,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 18,
                margin: 0,
                fontWeight: 600,
                color: "#000000",
              }}
            >
              Configurações de acesso e menu
            </h1>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: 11,
                color: "#444444",
              }}
            >
              Gerencie o pacote de usuários e quais módulos aparecem na LeftBar
              por departamento.
            </p>
          </div>
        </div>

        {/* 1) GESTÃO DE ACESSO POR DEPARTAMENTO / LICENÇAS */}
        <div
          style={{
            borderRadius: 8,
            background: "#ffffff",
            border: "1px solid rgba(0,0,0,0.08)",
            padding: 14,
            boxSizing: "border-box",
          }}
        >
          {/* Barra de progresso do pacote */}
          <div
            style={{
              marginBottom: 12,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                color: "#111827",
              }}
            >
              <span>
                Uso de licenças do pacote{" "}
                <strong>({USERS_LIMIT} usuários inclusos)</strong>
              </span>
              <span>
                <strong>
                  {totalUsers} / {USERS_LIMIT}
                </strong>{" "}
                usuários
              </span>
            </div>
            <div
              style={{
                width: "100%",
                height: 8,
                borderRadius: 999,
                background: "#e5e7eb",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${usagePct}%`,
                  height: "100%",
                  background: extraUsers > 0 ? "#b91c1c" : primaryColor,
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                color: "#555555",
              }}
            >
              <span>
                {extraUsers > 0 ? (
                  <>
                    <strong>+{extraUsers}</strong> usuário(s) adicional(is) ·
                    acréscimo de{" "}
                    <strong>
                      R$
                      {" " + extraCost.toFixed(2).replace(".", ",")}
                    </strong>{" "}
                    na mensalidade
                  </>
                ) : (
                  "Nenhum usuário adicional · sem acréscimo na mensalidade"
                )}
              </span>
            </div>
          </div>

          <div
            style={{
              marginBottom: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: 15,
                  margin: 0,
                  fontWeight: 500,
                  color: "#000000",
                }}
              >
                Usuários ativos no sistema
              </h2>
              <p
                style={{
                  fontSize: 11,
                  margin: "2px 0 0",
                  color: "#444444",
                }}
              >
                Abaixo estão os usuários que consomem licenças do pacote. Ajuste
                apenas o departamento quando necessário.
              </p>
            </div>
          </div>

          {/* Tabela de usuários */}
          <div
            style={{
              borderRadius: 8,
              border: "1px solid rgba(0,0,0,0.08)",
              background: "#ffffff",
              padding: 8,
              maxHeight: 260,
              overflowY: "auto",
            }}
          >
            {collaborators.length === 0 ? (
              <div
                style={{
                  fontSize: 12,
                  color: "#555555",
                }}
              >
                Nenhum usuário ativo configurado ainda.
              </div>
            ) : (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f3f4f6",
                      color: "#111827",
                    }}
                  >
                    <th
                      style={{
                        textAlign: "left",
                        padding: "6px 8px",
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                      }}
                    >
                      Usuário
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "6px 8px",
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                      }}
                    >
                      E-mail
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "6px 8px",
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                        width: "30%",
                      }}
                    >
                      Departamento
                    </th>
                    <th
                      style={{
                        textAlign: "center",
                        padding: "6px 8px",
                        borderBottom: "1px solid rgba(0,0,0,0.08)",
                        width: "10%",
                      }}
                    >
                      Remover
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {collaborators.map((col) => (
                    <tr key={col.id}>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        {col.nome}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                          color: "#4b5563",
                        }}
                      >
                        {col.email}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <select
                          value={col.departamentoSlug || ""}
                          onChange={(e) =>
                            handleUpdateCollaboratorDept(col.id, e.target.value)
                          }
                          style={{
                            width: "100%",
                            background: "#ffffff",
                            borderRadius: 999,
                            border: "1px solid rgba(0,0,0,0.18)",
                            padding: "4px 10px",
                            fontSize: 12,
                            color: "#000000",
                            outline: "none",
                          }}
                        >
                          <option value="">— Selecione —</option>
                          {menu?.map((dept, idx) => {
                            const slug = dept.slug || deptSlug(dept.name);
                            return (
                              <option key={idx} value={slug}>
                                {dept.name} ({slug})
                              </option>
                            );
                          })}
                        </select>
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                          textAlign: "center",
                        }}
                      >
                        <button
                          onClick={() => handleRemoveCollaborator(col.id)}
                          style={{
                            borderRadius: 999,
                            border: "1px solid rgba(0,0,0,0.18)",
                            background: "#ffffff",
                            fontSize: 11,
                            padding: "4px 10px",
                            cursor: "pointer",
                            color: "#b91c1c",
                          }}
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* 2) CONFIGURAÇÃO DO MENU POR DEPARTAMENTO */}
        <div
          style={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            gap: 12,
          }}
        >
          {/* COLUNA ESQUERDA: Departamentos */}
          <div
            style={{
              width: "28%",
              minWidth: 260,
              borderRadius: 8,
              background: "#ffffff",
              border: "1px solid rgba(0,0,0,0.08)",
              padding: 12,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 2,
              }}
            >
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar departamento / módulo / submódulo..."
                style={{
                  flex: 1,
                  background: "#ffffff",
                  borderRadius: 999,
                  border: "1px solid rgba(0,0,0,0.18)",
                  padding: "6px 12px",
                  fontSize: 12,
                  color: "#000000",
                  outline: "none",
                }}
              />
              {loading && (
                <span style={{ fontSize: 11, color: "#555555" }}>
                  Carregando...
                </span>
              )}
            </div>

            {error && (
              <div
                style={{
                  borderRadius: 8,
                  background: "#fff7f7",
                  border: "1px solid rgba(185,28,28,0.5)",
                  padding: "8px 10px",
                  fontSize: 12,
                  color: "#b91c1c",
                }}
              >
                <strong>Erro ao carregar menu:</strong> {error}
              </div>
            )}

            <div
              style={{
                flex: 1,
                overflowY: "auto",
                paddingRight: 2,
              }}
            >
              {!menu && !loading && !error && (
                <div
                  style={{
                    padding: 10,
                    fontSize: 12,
                    color: "#555555",
                  }}
                >
                  Nenhum dado carregado.
                </div>
              )}

              {menu &&
                filteredDeptIndices.map((deptIndex) => {
                  const dept = menu[deptIndex];
                  const isSelected = selectedDeptIndex === deptIndex;

                  const qtdModulos = dept.items.length;
                  const qtdSubmodulos = dept.items.reduce(
                    (acc, it) => acc + it.subitems.length,
                    0
                  );

                  return (
                    <button
                      key={deptIndex}
                      onClick={() => setSelectedDeptIndex(deptIndex)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "1px solid rgba(0,0,0,0.12)",
                        marginBottom: 6,
                        padding: "8px 10px",
                        borderRadius: 10,
                        cursor: "pointer",
                        backgroundColor: isSelected
                          ? "rgba(17,66,128,0.06)"
                          : "#ffffff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 2,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: "#000000",
                          }}
                        >
                          {dept.name}
                        </span>
                        {/* bolinha verde/vermelha */}
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: dept.hidden ? "#b91c1c" : "#16a34a",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "#555555",
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>
                          {qtdModulos} módulos · {qtdSubmodulos} submódulos
                        </span>
                        <span>
                          slug:{" "}
                          <code style={{ fontFamily: "monospace" }}>
                            {dept.slug || deptSlug(dept.name)}
                          </code>
                        </span>
                      </div>
                    </button>
                  );
                })}

              {menu && !filteredDeptIndices.length && !loading && !error && (
                <div
                  style={{
                    padding: 10,
                    fontSize: 12,
                    color: "#555555",
                  }}
                >
                  Nenhum departamento encontrado com esse filtro.
                </div>
              )}
            </div>
          </div>

          {/* COLUNA DIREITA: Módulos e submódulos do departamento selecionado */}
          <div
            style={{
              flex: 1,
              borderRadius: 8,
              background: "#ffffff",
              border: "1px solid rgba(0,0,0,0.08)",
              padding: 14,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {!selectedDept ? (
              <div
                style={{
                  fontSize: 13,
                  color: "#555555",
                }}
              >
                Selecione um departamento na lista à esquerda para visualizar e
                configurar os módulos.
              </div>
            ) : (
              <>
                {/* Cabeçalho do departamento selecionado */}
                <div
                  style={{
                    marginBottom: 6,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#555555",
                        marginBottom: 2,
                      }}
                    >
                      Departamento selecionado
                    </div>
                    <h2
                      style={{
                        fontSize: 15,
                        margin: 0,
                        fontWeight: 500,
                        color: "#000000",
                      }}
                    >
                      {selectedDept.name}
                    </h2>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        color: "#222222",
                      }}
                    >
                      <ToggleDot
                        checked={!selectedDept.hidden}
                        onToggle={() =>
                          selectedDeptIndex !== null &&
                          toggleDeptActive(selectedDeptIndex)
                        }
                      />
                      <span>Departamento ativo na LeftBar</span>
                    </div>
                  </div>
                </div>

                {/* Lista de módulos/submódulos */}
                <div
                  style={{
                    flex: 1,
                    borderRadius: 8,
                    border: "1px solid rgba(0,0,0,0.08)",
                    background: "#ffffff",
                    padding: 8,
                    overflowY: "auto",
                  }}
                >
                  {selectedDept.items.length === 0 ? (
                    <div
                      style={{
                        fontSize: 12,
                        color: "#555555",
                      }}
                    >
                      Não há módulos configurados para este departamento
                      (estrutura vem da API).
                    </div>
                  ) : (
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 12,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            background: "#f3f4f6", // cabeçalho sem azul
                            color: "#111827",
                          }}
                        >
                          <th
                            style={{
                              textAlign: "left",
                              padding: "6px 8px",
                              borderBottom: "1px solid rgba(0,0,0,0.08)",
                            }}
                          >
                            Módulo / Submódulo
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "6px 8px",
                              borderBottom: "1px solid rgba(0,0,0,0.08)",
                              width: "25%",
                            }}
                          >
                            Código
                          </th>
                          <th
                            style={{
                              textAlign: "center",
                              padding: "6px 8px",
                              borderBottom: "1px solid rgba(0,0,0,0.08)",
                              width: "15%",
                            }}
                          >
                            Ativo
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDept.items.map((mod, itemIndex) => (
                          <React.Fragment key={itemIndex}>
                            {/* Linha do módulo */}
                            <tr>
                              <td
                                style={{
                                  padding: "6px 8px",
                                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                                  fontWeight: 500,
                                  color: "#000000",
                                }}
                              >
                                {mod.label}
                              </td>
                              <td
                                style={{
                                  padding: "6px 8px",
                                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                                  fontFamily: "monospace",
                                  color: "#222222",
                                }}
                              >
                                {mod.codigo || "—"}
                              </td>
                              <td
                                style={{
                                  padding: "6px 8px",
                                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                                  textAlign: "center",
                                }}
                              >
                                <ToggleDot
                                  checked={!mod.hidden}
                                  onToggle={() =>
                                    selectedDeptIndex !== null &&
                                    toggleMainActive(
                                      selectedDeptIndex,
                                      itemIndex
                                    )
                                  }
                                />
                              </td>
                            </tr>

                            {/* Linhas dos submódulos */}
                            {mod.subitems.map((sub, subIndex) => (
                              <tr key={`${itemIndex}-${subIndex}`}>
                                <td
                                  style={{
                                    padding: "4px 8px 4px 24px",
                                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                                    color: "#222222",
                                  }}
                                >
                                  • {sub.label}
                                </td>
                                <td
                                  style={{
                                    padding: "4px 8px",
                                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                                    fontFamily: "monospace",
                                    color: "#444444",
                                  }}
                                >
                                  {sub.codigo || "—"}
                                </td>
                                <td
                                  style={{
                                    padding: "4px 8px",
                                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                                    textAlign: "center",
                                  }}
                                >
                                  <ToggleDot
                                    checked={!sub.hidden}
                                    onToggle={() =>
                                      selectedDeptIndex !== null &&
                                      toggleSubActive(
                                        selectedDeptIndex,
                                        itemIndex,
                                        subIndex
                                      )
                                    }
                                  />
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div
                  style={{
                    marginTop: 6,
                    fontSize: 11,
                    color: "#555555",
                  }}
                >
                  Esta tela não altera a estrutura da API, apenas marca quais
                  módulos e submódulos serão exibidos. Use o JSON exportado para
                  aplicar esses{" "}
                  <code style={{ fontFamily: "monospace" }}>hidden</code> na
                  LeftBar antes de montar o menu.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuConfigPage;
